import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AccountSearch, Loader, AnimalSearch, Modal, DateInput, SpecimenOwnershipBreakdown, BackButton } from '../../components';
import PlusCircleSvg from '../../components/svgs/PlusCircle.svg';
import AddAnimal from '../animal-management/AddAnimal';
import { OwnerItemDistribution } from '../../components/specimen-ownership-breakdown/SpecimenOwnershipBreakdown';
import FilterDropdown, { FilterOption, defaultFilterOption } from '../../components/custom-input/FilterDropdown';
import ValidatedInput, { ValidatedState } from '../../components/custom-input/ValidatedInput';
import { AlertSVG } from '../../components/svgs';
import SelectStorageLocations from '../../components/select-storage-locations/SelectStorageLocations';
import {
  Account,
  Animal,
  AnimalOwner,
  InventoryTransaction,
  SpecimenType,
  StorageLocation,
  StorageSite,
  UserInfo,
} from '../../types/interfaces';
import { RoleOptions, StaffInfo, StaffMember } from '../../types/interfaces/staffMember.interfaces';
import { QualityType, TransactionTypeEnum, Validators, specimenUOM } from '../../types/enums';
import { SpecimenCustomDataKey, SpecimenCustomDataValue } from '../../types/interfaces/specimenCustomDataKeyModel.interfaces';
import { toastMessages } from '../../constants/errorMessages';
import { ROUTE_PATHS } from '../../constants/routePaths';
import {
  BUTTON_CONSTANTS,
  INVENTORY_CONSTANTS,
  LABEL_CONSTANTS,
  PAGE_HEADER_CONSTANTS,
  TRANSACTION_CONSTANTS,
  VALIDATION_ALERT_CONSTANTS,
} from '../../constants/common';
import { showToast } from '../../services/toast.service';
import { requestRsgUsers } from '../../services/user.service';
import { requestPendingDepositTransactionsByAccountId } from '../../services/inventoryTransaction.service';
import { createCollectTransaction, isCollectDepositValid, updateDepositTransaction } from '../../services/collectDeposit.service';
import { cleanCustomData, handleCustomDataStateChange } from '../../services/customData.service';
import { getSpecimenTypes } from '../../api/specimensTypesApi';
import { getStorageSites } from '../../api/storageItemsApi';
import { getCustomDataKeys } from '../../api/customDataPoints.api';
import { getAnimalById } from '../../api/animalsApi';
import { getSpecimens } from '../../api/specimensApi';
import { clampValue, hashCode, redirectToTopOfThePage } from '../../utils/commonUtils';
import { loadRoleOptions } from '../../utils/staffHelpers';
import EmbryoDetailsTable, { hasDuplicateStrawNumbers, StrawRow } from '../../components/embryo-table/EmbryoDetailsTable';
import { METHOD_OPTIONS } from '../../constants/data';
import AddStaffMember from '../animal-management/AddStaffMember';

export type CollectDepositSelectedStates = {
  animal?: Animal;
  account?: Account;
  collectedBy?: FilterOption<UserInfo>;
  freezeLocation?: FilterOption<StorageSite>;
  processedBy?: FilterOption<UserInfo>;
  frozenBy?: FilterOption<UserInfo>;
  veterinarian?: FilterOption<UserInfo>;
  embryologist?: FilterOption<UserInfo>;
  technician?: FilterOption<UserInfo>;
  quantity?: ValidatedState;
  qualityType?: FilterOption<QualityType>;
  specimensType?: FilterOption<SpecimenType>;
  depositTransaction?: FilterOption<InventoryTransaction>;
  specimenUOM?: FilterOption<specimenUOM>;
  qualityPercentage?: number | null;
  freezeDate?: Date;
  notes?: string;
  storageLocations?: StorageLocation[];
  ownerItemDistribution?: OwnerItemDistribution[];
  specimenCustomDataValues?: SpecimenCustomDataValue[];
  lotNumber?: ValidatedState;
  itemType?: FilterOption<string>;
  embryoStraws?: StrawRow[];
  veterinarianName?: string;
  embryologistName?: string;
  technicianName?: string;
};

export const initialStorageLocationState = {
  site: { name: '', value: '' },
  tank: { name: '', value: '' },
  canister: { name: '', value: '' },
  quantity: { value: 0 },
  id: hashCode(new Date().toString()),
};

interface OwnerStrawEntry {
  ownerAccountId: string;
  strawData: StrawRow[];
}

const MAX_STRAWS = 100;

export const RequireAlert: FC<{ isActive: boolean }> = ({ isActive }) => {
  return isActive ? (
    <div className="validated-input-message-error">
      <AlertSVG />
      {VALIDATION_ALERT_CONSTANTS.REQUIRED_FIELD}
    </div>
  ) : (
    <></>
  );
};

const InventoryManagementCollectDeposit = (): JSX.Element => {
  const { state: locationState } = useLocation();
  const navigate = useNavigate();
  const transaction: InventoryTransaction | undefined = useLocation().state;

  const [accountWasCleared, setAccountWasCleared] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [transactionType, setTransactionType] = useState<TransactionTypeEnum>(TransactionTypeEnum.Default);
  const [transactionDepositNotes, setTransactionDepositNotes] = useState<string>('');
  const [pendingDepositTransactions, setPendingDepositTransactions] = useState<FilterOption<InventoryTransaction>[]>([]);
  const [specimensType, setSpecimensType] = useState<FilterOption<SpecimenType>[]>([]);
  const [rsgUsers, setRsgUsers] = useState<FilterOption<UserInfo>[]>([]);
  const [freezeLocations, setFreezeLocations] = useState<FilterOption<StorageSite>[]>([]);
  const [addAnimalModal, setAddAnimalModal] = useState(false);
  const [childKey, setChildKey] = useState<number>(0);
  const [isValidationAlertActive, setIsValidationAlertActive] = useState<boolean>(false);
  const [animalAutofill, setAnimalAutofill] = useState<Animal | undefined>();
  const [collectingInventory, setCollectingInventory] = useState<boolean>(true);
  const [lotNumberUnique, setLotNumberUnique] = useState<boolean>(true);
  const [selectedStates, setSelectedStates] = useState<CollectDepositSelectedStates>({
    storageLocations: [initialStorageLocationState],
  });
  const [quantityError, setQuantityError] = useState<string | null>(null);
  const [numStraws, setNumStraws] = useState<number>(0);
  const [strawData, setStrawData] = useState<StrawRow[]>([]);
  const [ownerQtyErrors, setOwnerQtyErrors] = useState<Record<string, boolean>>({});
  const [strawValidationError, setStrawValidationError] = useState<string | null>(null);
  const [ownerStrawData, setOwnerStrawData] = useState<OwnerStrawEntry[]>([]);
  const [missingStrawError, setMissingStrawError] = useState<string | null>(null);
  const [numStrawsError, setNumStrawsError] = useState<string | null>(null);
  const [prevNumStraws, setPrevNumStraws] = useState<number>(0);
  const [openDesignationModal, setOpenDesignationModal] = useState(false);
  const [designationInfo, setDesignationInfo] = useState<StaffInfo>({
    firstName: '',
    lastName: '',
    designation: [],
  });
  const [rsgOptions, setRsgOptions] = useState<FilterOption<UserInfo>[]>([]);

  const [roleOptions, setRoleOptions] = useState<RoleOptions>({
    vet: [],
    embryologist: [],
    tech: [],
  });

  const isEmbryo = selectedStates?.specimensType?.name === 'Embryo';

  const getOwnerId = (owner: any) => owner.account?.accountId ?? hashCode(owner.account?.name ?? '');

  useEffect(() => {
    const owners = selectedStates.animal?.owners;

    if (!owners || owners.length === 0 || selectedStates.specimensType?.name !== 'Embryo') {
      setOwnerStrawData([]);
      return;
    }
    setOwnerStrawData(prev => {
      const existingIds = prev.map(p => p.ownerAccountId);
      const newOwners = owners.filter(o => !existingIds.includes(getOwnerId(o)));

      const newEntries = newOwners.map(o => ({
        ownerAccountId: getOwnerId(o),
        strawData: [],
      }));

      return [...prev, ...newEntries];
    });
  }, [selectedStates.animal?.owners, selectedStates.specimensType?.name]);

  useEffect(() => {
    if (selectedStates?.animal?.owners?.length! < 1 && transactionType === TransactionTypeEnum.Collect) {
      showToast.error(toastMessages.NO_OWNERS_FOUND);
    }
  }, [selectedStates?.animal?.owners]);

  const handleGetPendingDepositTransactions = useCallback(async () => {
    try {
      setSelectedStates(prevStates => ({ ...prevStates, depositTransaction: undefined }));
      setPendingDepositTransactions([]);
      setTransactionDepositNotes('');

      if (selectedStates?.account?.accountId) {
        const { data: requestedPendingDepositTransactions } = (await requestPendingDepositTransactionsByAccountId(
          selectedStates?.account.accountId,
        )) as { data: InventoryTransaction[] };

        const mappedPendingDepositTransactions = requestedPendingDepositTransactions.map(transaction => ({
          name: ` ${transaction.transactionType} - ${new Date(transaction.createdDatetime).toLocaleDateString()} - Qty: ${
            transaction.changeQuantity
          }`,
          value: transaction,
        }));

        setPendingDepositTransactions(mappedPendingDepositTransactions);
      }
    } catch {
      showToast.error(toastMessages.SOMETHING_WENT_WRONG);
    }
  }, [selectedStates?.account]);

  const fetchRoleOptions = useCallback(async () => {
    if (!isEmbryo) {
      setRoleOptions({ vet: rsgOptions, embryologist: rsgOptions, tech: rsgOptions });
      return;
    }

    try {
      const roleOption = await loadRoleOptions(rsgOptions);
      setRoleOptions(roleOption);
    } catch {
      showToast.error(toastMessages.SOMETHING_WENT_WRONG);
    }
  }, [selectedStates?.specimensType?.name, rsgOptions]);

  useEffect(() => {
    fetchRoleOptions();
  }, [fetchRoleOptions]);

  useEffect(() => {
    const animal = selectedStates.animal;
    if (!animal) return;
    if (specimensType.length === 0) return;

    const isFemale = animal?.gender?.toLowerCase() === 'female';
    const selectedAnimalType = isFemale ? 'Embryo' : 'Semen';
    const matchedSpecimenType = specimensType.find(opt => opt.name?.toLowerCase() === selectedAnimalType.toLowerCase());

    if (matchedSpecimenType) {
      setSelectedStates(prev => ({ ...prev, specimensType: matchedSpecimenType }));
    }
  }, [selectedStates.animal?.animalId, specimensType]);

  const StrawDistributionSection = (
    <div className="straw-distribution-section">
      <label className="form-label">*{INVENTORY_CONSTANTS.STRAW_DISTRIBUTION}:</label>
      <EmbryoDetailsTable
        data={strawData}
        onChange={updatedRows => {
          setStrawData(updatedRows);

          const totalQuantity = updatedRows.reduce((acc, s) => acc + (s.embryoCountPerStraw || 0), 0);
          setSelectedStates(prev => ({
            ...prev,
            quantity: {
              value: totalQuantity.toString(),
              valid: totalQuantity > 0,
            },
          }));
        }}
      />

      {strawValidationError && (
        <div className="validated-input-message-error">
          <AlertSVG />
          {strawValidationError}
        </div>
      )}

      {quantityError && (
        <div className="validated-input-message-error">
          <AlertSVG />
          {quantityError}
        </div>
      )}
    </div>
  );

  useEffect(() => {
    handleGetPendingDepositTransactions();
  }, [handleGetPendingDepositTransactions]);

  const handlePageLoad = useCallback(async () => {
    try {
      const [{ data: reqItemType }, { data: reqSiteLocations }, { data: reqRsgUsers }] = await Promise.all([
        getSpecimenTypes(),
        getStorageSites({ filter: 'Active eq true' }),
        requestRsgUsers(),
      ]);

      const mappedItemType = reqItemType.map((item: SpecimenType) => {
        return { name: item.name ?? '', value: item };
      });

      const mappedSiteLocations = (reqSiteLocations as StorageSite[]).map((item: StorageSite) => {
        return { name: item.name ?? '', value: item };
      });

      const usersWithNames = reqRsgUsers.filter(user => user.firstName || user.lastName);

      const mappedRsgUsers = usersWithNames.map(item => {
        return { name: `${item.firstName ?? 'Error'} ${item.lastName ?? 'Error'}`, value: item };
      });

      setSpecimensType(mappedItemType);
      setFreezeLocations(mappedSiteLocations);
      setRsgUsers(mappedRsgUsers);
      setRsgOptions(mappedRsgUsers);
      setIsLoading(false);
    } catch (error) {
      showToast.error(toastMessages.SOMETHING_WENT_WRONG);
    }
  }, []);

  useEffect(() => {
    handlePageLoad();
  }, [handlePageLoad]);

  let strawCodeOptions: FilterOption<string>[] = [];

  if (selectedStates?.specimensType?.name === 'Embryo') {
    if (transactionType === TransactionTypeEnum.Collect) {
      strawCodeOptions = ownerStrawData
        .flatMap((owner: OwnerStrawEntry) => owner.strawData)
        .map((straw: StrawRow) => ({
          name: `${straw.embryoStrawNumber}${straw.embryoCountPerStraw ? ` (Qty: ${straw.embryoCountPerStraw})` : ''}`,
          value: straw.embryoStrawNumber,
          quantity: straw.embryoCountPerStraw,
        }));
    } else if (transactionType === TransactionTypeEnum.Deposit) {
      strawCodeOptions = strawData.map((straw: StrawRow) => ({
        name: `${straw.embryoStrawNumber}${straw.embryoCountPerStraw ? ` (Qty: ${straw.embryoCountPerStraw})` : ''}`,
        value: straw.embryoStrawNumber,
        quantity: straw.embryoCountPerStraw,
      }));
    }
  }

  const getOwnerQtyErrors = useCallback((): Record<string, boolean> => {
    const errors: Record<string, boolean> = {};

    if (
      transactionType !== TransactionTypeEnum.Collect ||
      selectedStates?.specimensType?.name !== 'Embryo' ||
      Number(numStraws) <= 0
    ) {
      return errors;
    }

    selectedStates?.animal?.owners?.forEach(owner => {
      const ownerId = getOwnerId(owner);
      const ownerData = ownerStrawData.find(ownerEntry => ownerEntry.ownerAccountId === ownerId);
      const totalQtyForOwner = ownerData?.strawData?.reduce((sum, straw) => sum + (straw.embryoCountPerStraw || 0), 0) || 0;
      const ownerOwnershipQty = Number(
        selectedStates.ownerItemDistribution?.find(distribution => getOwnerId(distribution.owner) === ownerId)?.ownedQuantity ??
          0,
      );

      if (totalQtyForOwner !== ownerOwnershipQty) {
        errors[ownerId] = true;
      }
    });

    return errors;
  }, [transactionType, selectedStates, ownerStrawData, numStraws]);

  useEffect(() => {
    setOwnerQtyErrors(getOwnerQtyErrors());
  }, [getOwnerQtyErrors]);

  const isValidForm = useCallback(() => {
    if (!selectedStates) return false;
    const isLotNumberValid = !selectedStates.lotNumber?.value || lotNumberUnique;
    const isOwnerStrawQtyValid = Object.keys(getOwnerQtyErrors()).length === 0;
    return isLotNumberValid && isCollectDepositValid(selectedStates, transactionType) && isOwnerStrawQtyValid;
  }, [selectedStates, transactionType, lotNumberUnique, getOwnerQtyErrors]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    setStrawValidationError(null);
    setNumStrawsError(null);

    if (selectedStates?.specimensType?.name === 'Embryo') {
      if (transactionType === TransactionTypeEnum.Collect) {
        const invalidOwner = ownerStrawData.find(ownerEntry =>
          ownerEntry.strawData.some((straw: StrawRow) => !straw.embryoCountPerStraw || straw.embryoCountPerStraw <= 0),
        );
        if (invalidOwner) {
          setStrawValidationError(VALIDATION_ALERT_CONSTANTS.MISSING_QUANTITY_COLLECT);
          return;
        }
        const allOwnerStraws = ownerStrawData.flatMap(ownerEntry => ownerEntry.strawData);
        if (hasDuplicateStrawNumbers(allOwnerStraws)) {
          setStrawValidationError(VALIDATION_ALERT_CONSTANTS.UNIQUE_STRAW_NUMBER);
          return;
        }
      } else {
        const invalidRows = strawData.some(strawRow => !strawRow.embryoCountPerStraw || strawRow.embryoCountPerStraw <= 0);
        if (invalidRows) {
          setStrawValidationError(VALIDATION_ALERT_CONSTANTS.MISSING_QUANTITY);
          return;
        }
        if (hasDuplicateStrawNumbers(strawData)) {
          setStrawValidationError(VALIDATION_ALERT_CONSTANTS.UNIQUE_STRAW_NUMBER);
          return;
        }
      }
    }

    if (!selectedStates) return;

    if (transactionType === TransactionTypeEnum.Collect) {
      const errors = getOwnerQtyErrors();
      setOwnerQtyErrors(errors);

      if (Object.keys(errors).length > 0) {
        setIsValidationAlertActive(true);
        return;
      }
    }

    if (!collectingInventory) return;
    setCollectingInventory(false);

    if (isValidForm() && selectedStates) {
      try {
        cleanCustomData(selectedStates.specimenCustomDataValues);
        setIsLoading(true);

        let strawPayload: StrawRow[] = [];

        if (selectedStates?.specimensType?.name === 'Embryo') {
          if (transactionType === TransactionTypeEnum.Collect) {
            strawPayload = ownerStrawData.flatMap(ownerEntry =>
              ownerEntry.strawData.map(straw => {
                let matchedCanisterId: number | undefined;

                selectedStates?.storageLocations?.forEach(location => {
                  const strawCodes = (location as any)?.strawCodesText?.split(',').map((id: string) => id.trim()) ?? [];
                  if (strawCodes.includes(straw.embryoStrawNumber)) {
                    matchedCanisterId = location.canister?.value
                      ? Number(location.canister.value)
                      : location.canister
                      ? Number(location.canister)
                      : undefined;
                  }
                });

                return {
                  ...straw,
                  storageCanisterId: matchedCanisterId,
                  accountId: ownerEntry.ownerAccountId,
                };
              }),
            );
          } else {
            strawPayload = strawData.map(straw => {
              let matchedCanisterId: number | undefined;

              selectedStates?.storageLocations?.forEach(location => {
                const strawCodes = (location as any)?.strawCodesText?.split(',').map((id: string) => id.trim()) ?? [];
                if (strawCodes.includes(straw.embryoStrawNumber)) {
                  matchedCanisterId = location.canister?.value
                    ? Number(location.canister.value)
                    : location.canister
                    ? Number(location.canister)
                    : undefined;
                }
              });

              return {
                ...straw,
                storageCanisterId: matchedCanisterId,
              };
            });
          }
        }

        const missingCanisterStraws = strawPayload.filter(straw => !straw.storageCanisterId);

        if (missingCanisterStraws.length > 0) {
          setIsLoading(false);
          setMissingStrawError(VALIDATION_ALERT_CONSTANTS.MISSING_STORAGE_LOCATION);
          return;
        }

        const finalPayload = {
          ...selectedStates,
          embryoStraws: strawPayload,
        };

        if (transactionType === TransactionTypeEnum.Collect) {
          await createCollectTransaction(finalPayload);
          showToast.success(toastMessages.COLLECT_SUCCESSFUL);
        } else {
          await updateDepositTransaction(finalPayload);
          showToast.success(toastMessages.DEPOSIT_SUCCESSFUL);
        }

        handleClearState();
        setIsLoading(false);
      } catch (error) {
        showToast.error(toastMessages.SOMETHING_WENT_WRONG);
        setIsLoading(false);
      } finally {
        setCollectingInventory(true);
      }
    } else {
      setCollectingInventory(true);
      setIsValidationAlertActive(true);
      if (
        ((selectedStates?.ownerItemDistribution && selectedStates?.ownerItemDistribution?.length <= 0) ||
          selectedStates?.animal?.owners?.length! < 1) &&
        transactionType === TransactionTypeEnum.Collect
      ) {
        showToast.error(toastMessages.NO_OWNERS_FOUND);
      } else if (
        Number(selectedStates?.quantity?.value) !==
        selectedStates?.storageLocations?.reduce((a, b) => a + Number(b.quantity.value), 0)
      ) {
        showToast.error(toastMessages.TOTAL_QTY_MISMATCH);
      } else {
        showToast.error(toastMessages.MISSING_REQUIRED_FIELDS);
      }
    }
  };

  const handleClearState = () => {
    setSelectedStates({ storageLocations: [initialStorageLocationState] });
    setStrawData([]);
    setOwnerStrawData([]);
    setTransactionDepositNotes('');
    setPendingDepositTransactions([]);
    setSpecimensType([]);
    setRsgUsers([]);
    setFreezeLocations([]);
    setChildKey(prev => prev + 1);
    setIsValidationAlertActive(false);
    handlePageLoad();
    redirectToTopOfThePage();
  };

  useEffect(() => {
    async function getCustomDataPoints() {
      let customDataArray: SpecimenCustomDataValue[] = [];

      if (selectedStates.specimensType?.value.specimenTypeId) {
        const { data } = await getCustomDataKeys({
          filter: `specimenTypeId eq ${selectedStates.specimensType?.value.specimenTypeId}`,
          include: 'SpecimenType',
        });
        customDataArray = data.map((data: SpecimenCustomDataKey) => {
          return { specimenCustomDataKeyId: data.specimenCustomDataKeyId, specimenCustomDataKey: data, value: '' };
        });

        setSelectedStates(prevStates => ({ ...prevStates, specimenCustomDataValues: customDataArray }));
      }
    }
    getCustomDataPoints();
  }, [selectedStates.specimensType?.value.specimenTypeId]);

  const handlePendingDepositChange = (option: FilterOption<InventoryTransaction>) => {
    setSelectedStates(prevStates => ({ ...prevStates, depositTransaction: option }));
    setTransactionDepositNotes(option.value.notes ?? '');
  };

  const handleNumStrawsChange = (value: number) => {
    const nextValue = Math.max(0, Math.min(value, MAX_STRAWS));

    if (nextValue > 0) {
      setNumStraws(nextValue);
      setPrevNumStraws(nextValue);

      if (selectedStates.specimensType?.name === 'Embryo') {
        if (transactionType === TransactionTypeEnum.Deposit) {
          setStrawData(prev => {
            if (prev.length === nextValue) return prev;

            let updated = [...prev];

            if (nextValue > prev.length) {
              for (let index = prev.length; index < nextValue; index++) {
                updated.push({
                  embryoStrawNumber: (index + 1).toString(),
                  embryoGrade: '',
                  embryoStage: '',
                  embryoCountPerStraw: 0,
                  storageCanisterId: undefined,
                });
              }
            } else {
              updated = updated.slice(0, nextValue);
            }
            updated = updated.map((row: StrawRow, index: number) => ({
              ...row,
              embryoStrawNumber: (index + 1).toString(),
            }));

            return updated;
          });
        } else if (transactionType === TransactionTypeEnum.Collect) {
          setOwnerStrawData(prev => {
            const totalOwners = prev.length;
            if (totalOwners === 0) return prev;

            const currentTotal = prev.reduce((sum, ownerEntry) => sum + ownerEntry.strawData.length, 0);
            if (currentTotal === nextValue) return prev;

            const baseCount = Math.floor(nextValue / totalOwners);
            const remainder = nextValue % totalOwners;

            let globalIndex = 0;
            const updatedOwners = prev.map((ownerEntry, index) => {
              const targetCount = baseCount + (index < remainder ? 1 : 0);

              const newStrawData: StrawRow[] = [];
              for (let i = 0; i < targetCount; i++) {
                const existing = ownerEntry.strawData[i];
                newStrawData.push(
                  existing ?? {
                    embryoStrawNumber: '',
                    embryoGrade: '',
                    embryoStage: '',
                    embryoCountPerStraw: 0,
                    storageCanisterId: undefined,
                  },
                );
              }

              const resequenced = newStrawData.map((straw, indx) => ({
                ...straw,
                embryoStrawNumber: (globalIndex + indx + 1).toString(),
              }));

              globalIndex += targetCount;

              return { ...ownerEntry, strawData: resequenced };
            });

            return updatedOwners;
          });
        }
      }
    } else {
      setNumStraws(0);
      setStrawData([]);
      setOwnerStrawData(prev => prev.map(o => ({ ...o, strawData: [] })));
    }
  };

  const numStrawsTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

  const queueNumStrawsChange = (value: number) => {
    if (numStrawsTimeoutRef.current) {
      clearTimeout(numStrawsTimeoutRef.current);
    }
    numStrawsTimeoutRef.current = setTimeout(() => {
      handleNumStrawsChange(Math.min(value, MAX_STRAWS));
    }, 400);
  };

  useEffect(() => {
    return () => {
      if (numStrawsTimeoutRef.current) {
        clearTimeout(numStrawsTimeoutRef.current);
      }
    };
  }, []);

  useEffect(
    function populatePendingDeposit() {
      if (transaction?.transactionType) {
        setTransactionType(transaction?.transactionType);

        if (Boolean(pendingDepositTransactions.length)) {
          const found = pendingDepositTransactions.find(
            deposit => deposit.value.inventoryTransactionId === transaction?.inventoryTransactionId,
          );

          if (found) handlePendingDepositChange(found);
        }
      }
    },
    [transaction, pendingDepositTransactions],
  );

  const handleAddAnimalAutofill = async (animalId?: number) => {
    if (animalId !== undefined) {
      try {
        const { data: animal } = await getAnimalById(animalId.toString(), { include: 'Owners.Account' });
        setSelectedStates(prevStates => ({ ...prevStates, animal: animal }));
        setAnimalAutofill(animal);
      } catch {
        showToast.error(toastMessages.SOMETHING_WENT_WRONG);
      }
    }
  };

  const handleLotNumber = async (lotNumber: ValidatedState) => {
    setSelectedStates(prevStates => ({
      ...prevStates,
      lotNumber: { value: lotNumber?.value?.replace(/\s+/g, ''), valid: lotNumber?.valid && lotNumber?.value?.trim().length > 0 },
    }));

    if (lotNumber?.valid && lotNumber?.value?.trim().length > 0) {
      try {
        const response: any = await getSpecimens({ filter: 'lotNumber eq ' + lotNumber.value });
        const isUnique = !(response.data.length > 0);
        setLotNumberUnique(isUnique);
      } catch (error: any) {
        showToast.error(toastMessages.SOMETHING_WENT_WRONG);
      }
    } else {
      setLotNumberUnique(true);
    }
  };

  return (
    <>
      {isLoading && <Loader loaderSize="medium" pageLoader simple />}
      <div className="inventory-management inventory-management-collect">
        {locationState && locationState.transactionManagementFilterPreset && (
          <BackButton
            onClick={() =>
              navigate(ROUTE_PATHS.APP_TRANSACTION_MANAGEMENT, {
                state: { filterPreset: locationState.transactionManagementFilterPreset, filter: locationState.filter },
              })
            }
          />
        )}
        <div className="inventory-action card">
          <h1>{PAGE_HEADER_CONSTANTS.COLLECT_DEPOSIT_INVENTORY}</h1>
          <form action="submit" onSubmit={e => handleSubmit(e)}>
            <div className="form-row transaction-type-header">
              <div>
                <h2>{TRANSACTION_CONSTANTS.TRANSACTION_TYPE}</h2>
                <RequireAlert isActive={isValidationAlertActive && !transactionType} />
              </div>

              <div className="radio-group">
                <div>
                  <input type="radio" name="transactionType" onChange={() => setTransactionType(TransactionTypeEnum.Collect)} />
                  <label>{LABEL_CONSTANTS.COLLECT}</label>
                </div>
                <div>
                  <input
                    type="radio"
                    name="transactionType"
                    onChange={() => setTransactionType(TransactionTypeEnum.Deposit)}
                    checked={transactionType === TransactionTypeEnum.Deposit}
                  />
                  <label>{LABEL_CONSTANTS.DEPOSIT}</label>
                </div>
              </div>
            </div>
            <br />
            {transactionType === TransactionTypeEnum.Deposit && (
              <>
                <div className="form-row">
                  <label>
                    <div>
                      *{LABEL_CONSTANTS.ACCOUNT_NAME}:
                      <RequireAlert isActive={isValidationAlertActive && !selectedStates.account?.name} />
                    </div>
                  </label>
                  <div className="input-container">
                    <AccountSearch
                      populateAccountObject={{ transaction: transaction, accountWasCleared, setAccountWasCleared }}
                      key={childKey}
                      showIcon
                      onChange={(account: Account | undefined) => {
                        if (transactionDepositNotes !== '') setTransactionDepositNotes('');

                        setSelectedStates(prevStates => ({ ...prevStates, account: account }));
                      }}
                    />
                  </div>
                </div>
                <div className="form-row">
                  <label>
                    <div>
                      *{LABEL_CONSTANTS.PENDING_DEPOSIT}
                      <RequireAlert isActive={isValidationAlertActive && !selectedStates.depositTransaction} />
                    </div>
                  </label>
                  <div className="input-container">
                    <FilterDropdown
                      options={pendingDepositTransactions}
                      value={selectedStates?.depositTransaction ?? defaultFilterOption}
                      onChange={option => handlePendingDepositChange(option)}
                    />
                  </div>
                </div>
                {transactionDepositNotes && (
                  <div className="form-row expanded-text-area">
                    <label htmlFor="notes">{TRANSACTION_CONSTANTS.TRANSACTION_NOTES}</label>
                    <textarea
                      id="notes"
                      name="notes"
                      placeholder="Notes"
                      style={{ border: '1', padding: '0.5rem' }}
                      value={transactionDepositNotes}
                      disabled
                      rows={4}
                      cols={50}
                    />
                  </div>
                )}
              </>
            )}
            <h2>{LABEL_CONSTANTS.ITEM_INFO}</h2>
            <div>
              <div className="form-row">
                <label>
                  <div>
                    *{INVENTORY_CONSTANTS.ANIMAL}:
                    <RequireAlert isActive={isValidationAlertActive && !selectedStates.animal} />
                  </div>
                </label>
                <div className="input-container animal-container">
                  <AnimalSearch
                    key={childKey}
                    onChange={animal => setSelectedStates(prevStates => ({ ...prevStates, animal: animal }))}
                    showIcon
                    disabled={!transactionType}
                    includeOwners
                    includeCustomData
                    animalAutofill={animalAutofill}
                  />
                  <button type="button" className="button" disabled={!transactionType} onClick={() => setAddAnimalModal(true)}>
                    <div className="add-animal-button">
                      <PlusCircleSvg />
                      &nbsp;&nbsp;{BUTTON_CONSTANTS.ADD_ANIMAL}
                    </div>
                  </button>
                </div>
              </div>
              <div className="form-row">
                <label>
                  <div>
                    *{INVENTORY_CONSTANTS.ITEM_TYPE}:
                    <RequireAlert isActive={isValidationAlertActive && !selectedStates.specimensType} />
                  </div>
                </label>
                <div className="input-container">
                  <input
                    type="text"
                    disabled
                    placeholder="Type"
                    value={selectedStates.animal ? selectedStates.specimensType?.name ?? '-' : ''}
                  />
                </div>
              </div>

              {Boolean(selectedStates.specimenCustomDataValues?.length) && (
                <div className="custom-data-container" key={selectedStates.animal?.animalId}>
                  {selectedStates.specimenCustomDataValues?.map((data: SpecimenCustomDataValue, index: number) => {
                    if (data.specimenCustomDataKey?.active) {
                      const { isNumeric, keyName, required, minValue, maxValue, specimenTypeId, specimenCustomDataKeyId } =
                        data.specimenCustomDataKey;
                      return (
                        <div
                          className="custom-data form-row"
                          key={specimenTypeId + (selectedStates.specimensType?.name ?? '') + index}>
                          <label key={keyName}>
                            {required ? '*' : ''}
                            {keyName}:
                            <RequireAlert
                              isActive={isValidationAlertActive && data.specimenCustomDataKey.required && !data.value?.trim()}
                            />
                          </label>
                          <div className="input-container" key={specimenCustomDataKeyId! + index}>
                            {keyName.toLowerCase() === 'method (ivf/conv)' ? (
                              <FilterDropdown
                                placeholder="Select Method"
                                options={METHOD_OPTIONS}
                                value={
                                  data.value
                                    ? METHOD_OPTIONS.find(opt => opt.value === data.value) ?? defaultFilterOption
                                    : defaultFilterOption
                                }
                                onChange={option => {
                                  handleCustomDataStateChange(
                                    { value: option.value, valid: true },
                                    data,
                                    selectedStates.specimenCustomDataValues,
                                    setSelectedStates,
                                  );
                                }}
                              />
                            ) : (
                              <ValidatedInput
                                label={<></>}
                                type={`${isNumeric ? 'number' : 'text'}`}
                                placeholder={`${data.value ? data.value : isNumeric ? 'Numeric' : 'Text'}`}
                                min={minValue ?? undefined}
                                max={keyName.toLowerCase().includes('number of straws') ? MAX_STRAWS : maxValue ?? undefined}
                                validatedStateForAutoFill={{
                                  value: data.value ?? '',
                                  valid: true,
                                }}
                                setValidatedState={value => {
                                  const isNumberOfStraws = keyName.toLowerCase().includes('number of straws');
                                  const numericValue = Number(value.value) || 0;
                                  const nextValue = isNumberOfStraws && numericValue > MAX_STRAWS
                                    ? { ...value, value: String(MAX_STRAWS), valid: true }
                                    : value;

                                  handleCustomDataStateChange(
                                    nextValue,
                                    data,
                                    selectedStates.specimenCustomDataValues,
                                    setSelectedStates,
                                  );

                                  if (isNumberOfStraws) {
                                    queueNumStrawsChange(Number(nextValue.value) || 0);
                                  }
                                }}
                                validators={required ? [Validators.REQUIRED] : []}
                              />
                            )}

                            {keyName.toLowerCase().includes('number of straws') && numStrawsError && (
                              <div className="validated-input-message-error">
                                <AlertSVG /> {numStrawsError}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    }
                  })}
                </div>
              )}

              <div className="form-row">
                <label>
                  <div>
                    *{INVENTORY_CONSTANTS.ITEM_UNIT_MEASURE}:
                    <RequireAlert isActive={isValidationAlertActive && !selectedStates.specimenUOM} />
                  </div>
                </label>
                <div className="input-container">
                  <FilterDropdown
                    placeholder="Select UoM"
                    options={Object.values(specimenUOM).map(item => ({ name: item, value: item })) as FilterOption<specimenUOM>[]}
                    value={selectedStates?.specimenUOM ?? defaultFilterOption}
                    onChange={option => setSelectedStates(prevStates => ({ ...prevStates, specimenUOM: option }))}
                    disabled={!transactionType}
                  />
                </div>
              </div>
              {selectedStates?.specimensType?.name === 'Embryo' && Number(numStraws) > 0 ? (
                transactionType === TransactionTypeEnum.Deposit && StrawDistributionSection
              ) : selectedStates?.specimensType?.name === 'Semen' ? (
                <>
                  <div className="form-row">
                    <label>{INVENTORY_CONSTANTS.QUALITY}:</label>
                    <div className="input-container">
                      <FilterDropdown
                        placeholder="Quality Score"
                        disabled={!transactionType}
                        options={
                          Object.values(QualityType).map(item => ({
                            name: item,
                            value: item,
                          })) as FilterOption<QualityType>[]
                        }
                        value={selectedStates?.qualityType ?? defaultFilterOption}
                        onChange={option => setSelectedStates(prev => ({ ...prev, qualityType: option }))}
                      />
                    </div>
                  </div>
                  <div className="form-row">
                    <ValidatedInput
                      setValidatedState={e =>
                        setSelectedStates(prev => ({
                          ...prev,
                          qualityPercentage:
                            e.value.length > 0 && e.value !== '-' ? clampValue(0, 100, Number(e.value)) : undefined,
                        }))
                      }
                      placeholder={'Quality %'}
                      min={0}
                      max={100}
                      disabled={!transactionType}
                      label="Quality %:"
                      validators={[]}
                      type="number"
                      validatedStateForAutoFill={{
                        value: (selectedStates?.qualityPercentage ?? '').toString(),
                        valid: true,
                      }}
                    />
                  </div>
                </>
              ) : null}

              <div className="form-row">
                <label>
                  <div>
                    *{INVENTORY_CONSTANTS.LOT_DATE}:
                    <RequireAlert isActive={isValidationAlertActive && !selectedStates.freezeDate} />
                  </div>
                </label>
                <div className="input-container">
                  <DateInput
                    disabled={!transactionType}
                    value={selectedStates?.freezeDate}
                    onChange={(date: Date) => setSelectedStates(prevStates => ({ ...prevStates, freezeDate: date }))}
                    maxDate={new Date()}
                  />
                </div>
              </div>
              <div className="form-row">
                <ValidatedInput
                  setValidatedState={e => handleLotNumber(e)}
                  placeholder={'Lot Number'}
                  label="Lot Number:"
                  validators={[]}
                  type="text"
                  validatedStateForAutoFill={selectedStates.lotNumber}
                />
                {!lotNumberUnique && (
                  <div className="validated-input-message-error">
                    <AlertSVG /> {VALIDATION_ALERT_CONSTANTS.UNIQUE_LOT_NUMBER}
                  </div>
                )}
              </div>
              <div className="form-row">
                <label>{INVENTORY_CONSTANTS.COLLECTION_SITE}:</label>
                <div className="input-container">
                  <FilterDropdown
                    disabled={!transactionType}
                    options={freezeLocations}
                    value={selectedStates?.freezeLocation ?? defaultFilterOption}
                    onChange={option => setSelectedStates(prevStates => ({ ...prevStates, freezeLocation: option }))}
                  />
                </div>
              </div>
              <div className="form-row">
                <label>{isEmbryo ? INVENTORY_CONSTANTS.VETERINARIAN : INVENTORY_CONSTANTS.COLLECTED_BY}:</label>
                <div className="input-container">
                  <FilterDropdown
                    disabled={!transactionType}
                    placeholder="Select Employee"
                    options={isEmbryo ? roleOptions.vet : rsgOptions}
                    value={(isEmbryo ? selectedStates?.veterinarian : selectedStates?.collectedBy) ?? defaultFilterOption}
                    onChange={option =>
                      setSelectedStates(prevStates =>
                        isEmbryo ? { ...prevStates, veterinarian: option } : { ...prevStates, collectedBy: option },
                      )
                    }
                  />
                </div>
              </div>

              <div className="form-row">
                <label>{isEmbryo ? INVENTORY_CONSTANTS.EMBRYOLOGIST : INVENTORY_CONSTANTS.PROCESSED_BY}:</label>
                <div className="input-container">
                  <FilterDropdown
                    disabled={!transactionType}
                    placeholder="Select Employee"
                    options={isEmbryo ? roleOptions.embryologist : rsgOptions}
                    value={(isEmbryo ? selectedStates?.embryologist : selectedStates?.processedBy) ?? defaultFilterOption}
                    onChange={option =>
                      setSelectedStates(prevStates =>
                        isEmbryo ? { ...prevStates, embryologist: option } : { ...prevStates, processedBy: option },
                      )
                    }
                  />
                </div>
              </div>

              <div className="form-row">
                <label>{isEmbryo ? INVENTORY_CONSTANTS.TECHNICIAN : INVENTORY_CONSTANTS.FROZEN_BY}:</label>
                <div className="input-container staff-container">
                  <FilterDropdown
                    disabled={!transactionType}
                    placeholder="Select Employee"
                    options={isEmbryo ? roleOptions.tech : rsgOptions}
                    value={(isEmbryo ? selectedStates?.technician : selectedStates?.frozenBy) ?? defaultFilterOption}
                    onChange={option =>
                      setSelectedStates(prevStates =>
                        isEmbryo ? { ...prevStates, technician: option } : { ...prevStates, frozenBy: option },
                      )
                    }
                  />

                  {isEmbryo && (
                    <button
                      type="button"
                      className="button"
                      disabled={!transactionType}
                      onClick={() => setOpenDesignationModal(true)}>
                      <div className="add-staff-button">
                        <PlusCircleSvg />
                        &nbsp;&nbsp;{BUTTON_CONSTANTS.ADD_STAFFS}
                      </div>
                    </button>
                  )}
                </div>
              </div>

              <div className="form-row">
                <ValidatedInput
                  key={childKey}
                  disabled={!transactionType}
                  validate={isValidationAlertActive}
                  type="number"
                  label="*Quantity:"
                  placeholder="0"
                  min={0}
                  validatedStateForAutoFill={selectedStates?.quantity ?? { value: '', valid: false }}
                  setValidatedState={validatedValue =>
                    setSelectedStates(prevStates => ({ ...prevStates, quantity: validatedValue }))
                  }
                  validators={[Validators.REQUIRED]}
                />
              </div>
              <div className="form-row expanded-text-area">
                <label htmlFor="notes">{LABEL_CONSTANTS.NOTES}</label>
                <textarea
                  id="notes"
                  name="notes"
                  placeholder="Notes"
                  onChange={event => setSelectedStates(prevStates => ({ ...prevStates, notes: event.target.value }))}
                  value={selectedStates?.notes ?? ''}
                  rows={4}
                  cols={50}
                  disabled={!transactionType}
                />
              </div>
            </div>
            {transactionType === TransactionTypeEnum.Collect && (
              <>
                <h2>{INVENTORY_CONSTANTS.ITEM_OWNERSHIP_BREAKDOWN}</h2>
                {selectedStates?.animal?.owners?.length ? (
                  <SpecimenOwnershipBreakdown
                    animalOwners={selectedStates.animal.owners}
                    quantity={String(selectedStates?.quantity?.value ?? '')}
                    ownerItemDistribution={selectedStates.ownerItemDistribution ?? []}
                    setOwnerItemDistribution={e =>
                      setSelectedStates(prevStates => ({
                        ...prevStates,
                        ownerItemDistribution: e,
                      }))
                    }
                  />
                ) : null}

                {selectedStates?.specimensType?.name === 'Embryo' &&
                  Number(numStraws) > 0 &&
                  (selectedStates?.animal?.owners?.length ?? 0) > 0 && (
                    <>
                      <h2>{INVENTORY_CONSTANTS.STRAW_DISTRIBUTION}</h2>
                      {(() => {
                        let globalIndex = 0;
                        return selectedStates.animal!.owners!.map((owner: AnimalOwner, index: number) => {
                          const ownerId = getOwnerId(owner);
                          const ownerData = ownerStrawData.find(o => o.ownerAccountId === ownerId);
                          const strawData = ownerData?.strawData ?? [];

                          const startIndex = globalIndex;
                          globalIndex += strawData.length;

                          return (
                            <div key={ownerId} className="owner-straw-distribution">
                              <h3>{owner.account?.name ?? `Owner ${index + 1}`}</h3>

                              <EmbryoDetailsTable
                                data={strawData}
                                globalStartIndex={startIndex}
                                onChange={(updatedRows, valid) => {
                                  setOwnerStrawData(prev =>
                                    prev.map(o => (o.ownerAccountId === ownerId ? { ...o, strawData: updatedRows } : o)),
                                  );
                                }}
                              />
                              {strawValidationError && (
                                <div className="validated-input-message-error">
                                  <AlertSVG />
                                  {strawValidationError}
                                </div>
                              )}
                              {ownerQtyErrors[ownerId] && (
                                <div className="validated-input-message-error">
                                  <AlertSVG />
                                  <span>{VALIDATION_ALERT_CONSTANTS.TOTAL_QUANTITY_MISMATCH}</span>
                                </div>
                              )}
                            </div>
                          );
                        });
                      })()}
                    </>
                  )}
              </>
            )}

            <h2>{LABEL_CONSTANTS.STORAGE_LOCATION}</h2>
            {transactionType && (
              <SelectStorageLocations
                selectedStorageLocations={selectedStates?.storageLocations ?? []}
                isValidationAlertActive={isValidationAlertActive}
                setSelectedStorageLocations={newStorageLocations =>
                  setSelectedStates(prevStates => ({ ...prevStates, storageLocations: newStorageLocations }))
                }
                quantityToMatch={Number(selectedStates?.quantity?.value ?? 0)}
                activeLocationsOnly
                strawCodeOptions={strawCodeOptions}
                itemType={selectedStates?.specimensType?.name ?? ''}
              />
            )}
            {missingStrawError && (
              <div className="validated-input-message-error">
                <AlertSVG />
                {missingStrawError}
              </div>
            )}
            <div className="submit-button">
              <button type="submit" className={isValidForm() ? 'button small green' : 'disabled button small green'}>
                {BUTTON_CONSTANTS.SUBMIT}
              </button>
            </div>
          </form>
          <Modal isOpen={addAnimalModal} onClose={() => setAddAnimalModal(false)} width={1014} ignoreBackdrop>
            <AddAnimal
              onCancel={(animalId?: number) => {
                handleAddAnimalAutofill(animalId);
                setAddAnimalModal(false);
              }}
            />
          </Modal>
          <Modal isOpen={openDesignationModal} onClose={() => setOpenDesignationModal(false)} width={600} ignoreBackdrop>
            <AddStaffMember
              staffInfo={designationInfo}
              setStaffInfo={setDesignationInfo}
              onCancel={() => setOpenDesignationModal(false)}
              onSaved={fetchRoleOptions}
            />
          </Modal>
        </div>
      </div>
    </>
  );
};

export default InventoryManagementCollectDeposit;
